/**
 * The decision for a whole campaign, in the shape docs/c3/BACKEND.md (item 84) asks the backend to offer.
 * That endpoint does not exist yet: today a person decides one incident at a time by talking to that incident's
 * remediation agent over a websocket. Demo answers these requests with `decideCampaignPlanInDemo`, which keeps the
 * documented rules, so the screen is already written for the real answer and a Live adapter only has to replace it.
 *
 * Pure: no request, no clock apart from the `now` it is given.
 */
import type { CampaignPlanItem } from './selectors/campaignView';

export type CampaignDecision = 'approve' | 'reject';

export interface CampaignDecisionRequest {
  campaign_id: string;
  /** Made by the screen for each press, so a repeated request is told from a retry. */
  decision_id: string;
  decision: CampaignDecision;
  /** The remediations the person was looking at. Anything that joined the campaign since is not decided blind. */
  remediation_ids: string[];
}

/** `accepted`: decided now. `skipped`: already decided before, left as it is. `failed`: could not be decided, nothing changed. */
export type CampaignDecisionOutcome = 'accepted' | 'skipped' | 'failed';

export interface CampaignDecisionResult {
  remediation_id: string;
  threat_event_id: string;
  outcome: CampaignDecisionOutcome;
  /** The remediation's status after the request. */
  status: string;
  error?: { code: string; message: string };
}

export interface CampaignDecisionResponse {
  campaign_id: string;
  decision_id: string;
  decision: CampaignDecision;
  /** `complete`: every incident is decided. `partial`: some are, some failed. `failed`: none could be decided. */
  status: 'complete' | 'partial' | 'failed';
  decided_at: string;
  results: CampaignDecisionResult[];
  /** The campaign's plan after the request, computed by the backend. Demo leaves it out: the screen rolls it up itself. */
  plan?: { status: string; awaiting: number };
}

/** Sends a campaign decision and says how it went: Demo's simulation today, the backend's endpoint later. */
export type DecideCampaignPlan = (request: CampaignDecisionRequest) => Promise<CampaignDecisionResponse>;

/** What the rules of the contract decide for a request, given what is already decided. Shared by Demo and the tests. */
export function simulateCampaignDecision(
  request: CampaignDecisionRequest,
  items: CampaignPlanItem[],
  options: { decided?: ReadonlySet<string>; failing?: ReadonlySet<string>; now?: number } = {}
): CampaignDecisionResponse {
  const decided = options.decided ?? new Set<string>();
  const failing = options.failing ?? new Set<string>();
  const byRemediation = new Map(items.filter((item) => item.plan).map((item) => [item.plan!._id, item]));
  const after = request.decision === 'approve' ? 'confirmed' : 'cancelled';

  // One remediation at a time, in the order asked; a failure does not stop the rest and nothing is rolled back.
  const results = request.remediation_ids.flatMap((id): CampaignDecisionResult[] => {
    const item = byRemediation.get(id);
    if (!item) return []; // not part of this campaign: the contract ignores it
    if (decided.has(item.threatId)) {
      return [{ remediation_id: id, threat_event_id: item.threatId, outcome: 'skipped', status: after }];
    }
    if (failing.has(item.threatId)) {
      return [
        {
          remediation_id: id,
          threat_event_id: item.threatId,
          outcome: 'failed',
          status: item.status,
          error: { code: 'agent_unavailable', message: 'The remediation agent could not take the decision.' },
        },
      ];
    }
    return [{ remediation_id: id, threat_event_id: item.threatId, outcome: 'accepted', status: after }];
  });

  const failed = results.filter((r) => r.outcome === 'failed').length;
  const status = failed === 0 ? 'complete' : failed === results.length ? 'failed' : 'partial';
  return {
    campaign_id: request.campaign_id,
    decision_id: request.decision_id,
    decision: request.decision,
    status,
    decided_at: new Date(options.now ?? Date.now()).toISOString(),
    results,
  };
}

/** Demo's answer: the rules above, with nothing failing and nothing saved. */
export function decideCampaignPlanInDemo(items: CampaignPlanItem[], decided: ReadonlySet<string>): DecideCampaignPlan {
  return (request) => Promise.resolve(simulateCampaignDecision(request, items, { decided }));
}
